// src/components/event/NowPlayingPanel.jsx
import React, { useEffect, useRef, useState } from 'react'
import SyncedLyrics from './SyncedLyrics'

const STORAGE_KEY = 'event_player_progress_v1'

const NowPlayingPanel = ({
                             audioRef,
                             tracks,
                             trackIndex,
                             isPlaying,
                             onChangeTrack,
                         }) => {
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [isDragging, setIsDragging] = useState(false)

    const progressBarRef = useRef(null)

    const currentTrack = tracks[trackIndex] || {}
    const trackKey =
        currentTrack.id || currentTrack.src || `track-${trackIndex}`

    const formatTime = (time) => {
        if (!time || Number.isNaN(time)) return '0:00'
        const mins = Math.floor(time / 60)
        const secs = Math.floor(time % 60)
        return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0

    /** 공통 시크 함수 (실제 시간 이동 + 로컬스토리지 저장) */
    const seekTo = (time) => {
        const audio = audioRef?.current
        if (!audio || typeof time !== 'number') return

        const d = audio.duration || duration || 0
        if (!d) return

        const target = Math.min(Math.max(time, 0), d)

        audio.currentTime = target
        setCurrentTime(target)

        if (typeof window === 'undefined' || !trackKey) return
        try {
            const raw = localStorage.getItem(STORAGE_KEY)
            const map = raw ? JSON.parse(raw) : {}
            map[trackKey] = Math.floor(target)
            localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
        } catch {
            // ignore
        }
    }

    /** 트랙 변경 / 메타로드 / 타임업데이트 + 로컬스토리지 저장/복구 */
    useEffect(() => {
        const audio = audioRef?.current
        if (!audio) return

        const handleLoadedMeta = () => {
            const d = audio.duration || 0
            setDuration(d)

            if (typeof window === 'undefined' || !trackKey) {
                setCurrentTime(audio.currentTime || 0)
                return
            }

            try {
                const raw = localStorage.getItem(STORAGE_KEY)
                if (!raw) {
                    setCurrentTime(audio.currentTime || 0)
                    return
                }
                const map = JSON.parse(raw)
                const saved = map?.[trackKey]

                if (typeof saved === 'number' && saved > 0 && saved < d) {
                    audio.currentTime = saved
                    setCurrentTime(saved)
                } else {
                    setCurrentTime(audio.currentTime || 0)
                }
            } catch {
                setCurrentTime(audio.currentTime || 0)
            }
        }

        const handleTimeUpdate = () => {
            if (isDragging) return
            const t = audio.currentTime || 0
            setCurrentTime(t)

            if (typeof window === 'undefined' || !trackKey) return
            try {
                const raw = localStorage.getItem(STORAGE_KEY)
                const map = raw ? JSON.parse(raw) : {}
                const rounded = Math.floor(t)
                if (map[trackKey] !== rounded) {
                    map[trackKey] = rounded
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
                }
            } catch {
                // ignore
            }
        }

        // ⭐ 여기 추가: 곡 끝났을 때 다음 곡으로
        const handleEnded = () => {
            if (!tracks || tracks.length === 0) return
            const nextIndex = (trackIndex + 1) % tracks.length
            onChangeTrack(nextIndex)
        }

        audio.addEventListener('loadedmetadata', handleLoadedMeta)
        audio.addEventListener('timeupdate', handleTimeUpdate)
        audio.addEventListener('ended', handleEnded)

        return () => {
            audio.removeEventListener('loadedmetadata', handleLoadedMeta)
            audio.removeEventListener('timeupdate', handleTimeUpdate)
            audio.removeEventListener('ended', handleEnded)
        }
    }, [audioRef, trackKey, isDragging, tracks, trackIndex, onChangeTrack])

    /** progressBar 위치 → 시간 계산 */
    const getTimeFromClientX = (clientX) => {
        const audio = audioRef?.current
        const bar = progressBarRef.current
        if (!audio || !bar) return null

        const rect = bar.getBoundingClientRect()
        const x = clientX - rect.left
        const width = rect.width || 1
        const ratio = Math.min(Math.max(x / width, 0), 1)
        const d = audio.duration || duration || 0
        if (!d) return null
        return d * ratio
    }

    /** 클릭 시 시크 */
    const handleSeekBarClick = (e) => {
        const newTime = getTimeFromClientX(e.clientX)
        if (newTime == null) return
        seekTo(newTime)
    }

    /** 드래그 시작 (마우스) */
    const handleSeekBarMouseDown = (e) => {
        e.preventDefault()
        const newTime = getTimeFromClientX(e.clientX)
        if (newTime == null) return

        setIsDragging(true)
        seekTo(newTime)

        const handleMove = (ev) => {
            const nt = getTimeFromClientX(ev.clientX)
            if (nt == null) return
            seekTo(nt)
        }

        const handleUp = () => {
            setIsDragging(false)
            window.removeEventListener('mousemove', handleMove)
            window.removeEventListener('mouseup', handleUp)
        }

        window.addEventListener('mousemove', handleMove)
        window.addEventListener('mouseup', handleUp)
    }

    /** 터치 드래그 (모바일) */
    const handleSeekBarTouchStart = (e) => {
        const touch = e.touches[0]
        if (!touch) return
        const newTime = getTimeFromClientX(touch.clientX)
        if (newTime == null) return

        setIsDragging(true)
        seekTo(newTime)
    }

    const handleSeekBarTouchMove = (e) => {
        const touch = e.touches[0]
        if (!touch) return
        const newTime = getTimeFromClientX(touch.clientX)
        if (newTime == null) return
        seekTo(newTime)
    }

    const handleSeekBarTouchEnd = () => {
        setIsDragging(false)
    }

    return (
        <div className="sticky top-24 space-y-4">
            {/* 플레이어 전체 카드 */}
            <div className="group overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900/90 via-slate-800/90 to-slate-900/90 p-6 backdrop-blur-xl transition-all hover:border-white/20">
                {/* 상단: Now Playing + 타이틀/부제 + 상태 */}
                <div className="mb-4 flex items-start justify-between gap-3">
                    <div>
                        <p className="text-[0.65rem] uppercase tracking-[0.18em] text-rose-300">
                            Now Playing
                        </p>
                        <p className="mt-1 text-[0.95rem] font-semibold leading-snug text-white">
                            {currentTrack.title || 'Untitled track'}
                        </p>
                        <p className="mt-1 text-[0.75rem] text-slate-400">
                            {currentTrack.subtitle || currentTrack.artist || ''}
                        </p>
                    </div>

                    <div className="flex flex-col items-end gap-1">
                        <span className="rounded-full bg-white/5 px-3 py-1 text-[0.65rem] text-slate-300">
                            {isPlaying ? '재생중' : '일시정지중'}
                        </span>
                        <div className="mt-1 flex flex-wrap justify-end gap-1.5">
                            {tracks.map((track, idx) => (
                                <button
                                    key={track.src || idx}
                                    type="button"
                                    onClick={() => onChangeTrack(idx)}
                                    className={`h-6 min-w-[2.2rem] rounded-full px-2 text-[0.7rem] transition ${
                                        idx === trackIndex
                                            ? 'bg-rose-400 text-black shadow-[0_0_10px_rgba(248,113,113,0.9)]'
                                            : 'bg-white/10 text-slate-200 hover:bg-white/20'
                                    }`}
                                >
                                    T{idx + 1}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* 가운데: 가사 (클릭 시 시크) */}
                <div className="mb-4 overflow-hidden rounded-2xl bg-gradient-to-br from-rose-500/20 via-pink-500/15 to-purple-500/20">
                    <div className="max-h-56 overflow-y-auto px-4 py-3 sm:px-5 sm:py-4 inner-lyrics-scroll">
                        <SyncedLyrics
                            audioRef={audioRef}
                            lines={currentTrack.syncedLyrics || []}
                            onSeek={seekTo}
                            currentTime={currentTime}
                        />
                    </div>
                </div>

                {/* 진행바 + 시간 */}
                <div className="mb-4 space-y-2">
                    <div className="flex items-center justify-between text-[0.7rem] text-slate-400">
                        <span>
                            {formatTime(currentTime)} / {formatTime(duration)}
                        </span>
                    </div>
                    <div
                        ref={progressBarRef}
                        className="relative h-1.5 cursor-pointer overflow-hidden rounded-full bg-slate-700/50"
                        onClick={handleSeekBarClick}
                        onMouseDown={handleSeekBarMouseDown}
                        onTouchStart={handleSeekBarTouchStart}
                        onTouchMove={handleSeekBarTouchMove}
                        onTouchEnd={handleSeekBarTouchEnd}
                    >
                        <div
                            className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-rose-400 via-yellow-400 to-purple-400 transition-all"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>

                {/* 하단: 트랙 리스트 */}
                <div className="space-y-2 pt-2">
                    {tracks.map((track, i) => (
                        <button
                            key={track.src || i}
                            type="button"
                            onClick={() => onChangeTrack(i)}
                            className={`w-full rounded-xl p-3 text-left transition-all ${
                                i === trackIndex
                                    ? 'bg-gradient-to-r from-rose-500/20 via-pink-500/20 to-purple-500/20 ring-1 ring-rose-400/50'
                                    : 'bg-slate-800/30 hover:bg-slate-800/50'
                            }`}
                        >
                            <div className="flex items-center gap-3">
                                <div
                                    className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                                        i === trackIndex
                                            ? 'bg-rose-400/20'
                                            : 'bg-slate-700/50'
                                    }`}
                                >
                                    {i === trackIndex && isPlaying ? (
                                        <div className="flex gap-0.5">
                                            <div className="h-3 w-0.5 animate-pulse bg-rose-400" />
                                            <div
                                                className="h-3 w-0.5 animate-pulse bg-rose-400"
                                                style={{ animationDelay: '0.2s' }}
                                            />
                                            <div
                                                className="h-3 w-0.5 animate-pulse bg-rose-400"
                                                style={{ animationDelay: '0.4s' }}
                                            />
                                        </div>
                                    ) : (
                                        <span className="text-xs font-bold text-slate-400">
                                            {i + 1}
                                        </span>
                                    )}
                                </div>
                                <div className="flex-1 overflow-hidden">
                                    <p
                                        className={`truncate text-sm font-medium ${
                                            i === trackIndex
                                                ? 'text-rose-300'
                                                : 'text-slate-300'
                                        }`}
                                    >
                                        {track.title}
                                    </p>
                                    <p className="truncate text-xs text-slate-500">
                                        {track.artist || track.subtitle}
                                    </p>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default NowPlayingPanel
